import React, { useCallback, useState } from "react";
import { Button, Input, notification, Upload, Tooltip } from "antd";
import {
  PaperClipOutlined,
  SendOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { RcFile, UploadChangeParam } from "antd/lib/upload/interface";
import styles from "./renderComponents.module.less";
import {
  ACCEPTABLE_MEDIA_TYPES,
  MAX_UPLOAD_SIZE,
} from "~/components/support/constants/Media";

const MAX_ATTACHMENT_COUNT = 5;

interface ChatInputProps {
  onAttachmentsChange?: (files: RcFile[]) => void;
  attachmentFiles?: RcFile[];
  onSend?: (text: string, files: RcFile[]) => void;
  isActiveAttachment?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onAttachmentsChange,
  attachmentFiles = [],
  onSend,
  isActiveAttachment = true,
  placeholder = "Type here...",
}) => {
  const [inputValue, setInputValue] = useState("");
  const acceptableMediaExtensions = ACCEPTABLE_MEDIA_TYPES.map(
    (x) => x.extension,
  ).join(", ");

  const onFilesSelected = useCallback(
    (info: UploadChangeParam) => {
      const newSelectedFiles = info.fileList
        .filter((file) => file.originFileObj)
        .map((file) => file.originFileObj as RcFile);

      if (newSelectedFiles.length === 0) return;

      const attachmentFilesLength = attachmentFiles.length || 0;
      const availableSlots = MAX_ATTACHMENT_COUNT - attachmentFilesLength;

      if (availableSlots <= 0) {
        notification.destroy();
        notification.info({
          message: "Maximum 5 files can be attached",
        });
        return;
      }

      // Filter out files that already exist
      const uniqueNewFiles = newSelectedFiles.filter(
        (newFile) =>
          !attachmentFiles.some(
            (existingFile) => existingFile.name === newFile.name,
          ),
      );

      if (uniqueNewFiles.length === 0) {
        notification.error({
          message: "Selected files already exist",
        });
        return;
      }

      // Take only the available slots
      const filesToAdd = uniqueNewFiles.slice(0, availableSlots);

      // Validate file types
      const invalidFiles = filesToAdd.filter(
        (file) =>
          !ACCEPTABLE_MEDIA_TYPES.some((type) => type.type === file.type),
      );

      if (invalidFiles.length > 0) {
        notification.error({
          message: "Only media files are allowed",
        });
        return;
      }

      // Calculate total size including current files and new files
      const currentFilesSize = attachmentFiles
        .map((x) => x.size)
        .reduce((acc, currentValue) => (acc || 0) + (currentValue || 0), 0);

      const newFilesSize = filesToAdd
        .map((x) => x.size)
        .reduce((acc, currentValue) => (acc || 0) + (currentValue || 0), 0);

      const totalSize = currentFilesSize + newFilesSize;

      if (totalSize > MAX_UPLOAD_SIZE) {
        notification.info({
          message: "Files size exceeded the maximum limit",
        });
        return;
      }

      const newFiles = [...attachmentFiles, ...filesToAdd];

      if (onAttachmentsChange) {
        onAttachmentsChange(newFiles);
      }

      // Show notification if some files were excluded due to limit
      if (uniqueNewFiles.length > availableSlots) {
        notification.info({
          message: `Only ${availableSlots} files added due to attachment limit`,
        });
      }
    },
    [attachmentFiles, onAttachmentsChange],
  );

  const handleRemoveAttachment = useCallback(
    (fileToRemove: RcFile) => {
      const newFiles = attachmentFiles.filter(
        (file) => file.uid !== fileToRemove.uid,
      );
      if (onAttachmentsChange) {
        onAttachmentsChange(newFiles);
      }
    },
    [attachmentFiles, onAttachmentsChange],
  );

  const isImageFile = useCallback((file: RcFile) => {
    return file.type.startsWith("image/");
  }, []);

  const getFilePreviewUrl = useCallback(
    (file: RcFile) => {
      if (isImageFile(file)) {
        return URL.createObjectURL(file);
      }
      return null;
    },
    [isImageFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && inputValue.trim()) {
        onSend?.(inputValue, attachmentFiles);
        setInputValue("");
      }
    },
    [inputValue, attachmentFiles, onSend],
  );

  const handleSendClick = useCallback(() => {
    if (inputValue.trim()) {
      onSend?.(inputValue, attachmentFiles);
      setInputValue("");
    }
  }, [inputValue, attachmentFiles, onSend]);

  return (
    <div className={styles.chatInputContainer}>
      {/* Attachment chips */}
      {attachmentFiles.length > 0 && (
        <div className={styles.attachmentChips}>
          {attachmentFiles.map((file) => {
            const previewUrl = getFilePreviewUrl(file);

            return (
              <Tooltip
                key={file.uid}
                title={
                  previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={file.name}
                      className={styles.thumbnailImage}
                    />
                  ) : null
                }
                placement="top"
                overlayClassName={styles.attachmentTooltip}
              >
                <div className={styles.attachmentChip}>
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt={file.name}
                      className={styles.chipThumbnail}
                    />
                  )}
                  <span className={styles.chipFileName}>
                    {file.name.length > 12
                      ? `${file.name.substring(0, 12)}...`
                      : file.name}
                  </span>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => handleRemoveAttachment(file)}
                    className={styles.chipRemoveButton}
                  />
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}

      <div className={styles.inputWrapper}>
        {isActiveAttachment && (
          <Upload
            accept={acceptableMediaExtensions}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={onFilesSelected}
            multiple
            fileList={[]}
          >
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              className={styles.attachmentButton}
              title="Add attachment"
            />
          </Upload>
        )}

        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={styles.inputField}
          bordered={false}
          autoFocus={true}
        />

        <Button
          type="text"
          icon={<SendOutlined />}
          onClick={handleSendClick}
          disabled={!inputValue.trim()}
          className={styles.sendButton}
        />
      </div>
    </div>
  );
};

export default ChatInput;
